import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  CheckCircle,
  Warning,
  PaperPlaneTilt,
  User,
  Brain,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { footerContactSchema } from "@/schemas/contact";
import { formatValidationError } from "@/lib/validation-errors";

type Audience = "client" | "therapist";
type Status = "idle" | "submitting" | "success" | "error";

interface FormState {
  name: string;
  email: string;
  audience: Audience;
  message: string;
  website: string; // honeypot
}

const EMPTY: FormState = {
  name: "",
  email: "",
  audience: "client",
  message: "",
  website: "",
};

export function GeneralContactForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    setErrorMessage(null);

    // Ista shema kao na serveru (trimuje), da unos koji prođe u browseru ne padne na serveru.
    // Honeypot se ne provjerava ovdje, server ga obrađuje tiho.
    if (!form.website) {
      const check = footerContactSchema.safeParse(form);
      if (!check.success) {
        setErrorMessage(formatValidationError(check.error.issues, form).error);
        setStatus("error");
        return;
      }
    }
    setStatus("submitting");

    try {
      const res = await fetch("/api/contact/footer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setErrorMessage(
          res.status === 429
            ? t("kontakt.form.rateLimited")
            : typeof body?.error === "string" && body.error
              ? body.error
              : null,
        );
        setStatus("error");
        return;
      }
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div
        className="relative overflow-hidden rounded-[1.75rem] bg-white p-10 sm:p-12"
        style={{
          border: "1px solid var(--color-warm-border)",
          boxShadow: "0 28px 60px -36px oklch(37.12% 0.059 186.62 / 0.28)",
        }}
      >
        <AccentEdge />
        <div
          className="inline-flex size-14 items-center justify-center rounded-2xl"
          style={{
            background: "var(--color-brand-bg-light)",
            color: "var(--color-brand-dark)",
          }}
        >
          <CheckCircle weight="duotone" className="size-7" />
        </div>
        <h3 className="font-heading mt-6 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {t("footer.contactForm.successTitle")}
        </h3>
        <p className="mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
          {t("footer.contactForm.successBody")}
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-7 inline-flex items-center gap-2 text-sm font-semibold underline-offset-4 transition-colors hover:underline"
          style={{ color: "var(--color-brand-dark)" }}
        >
          {t("footer.contactForm.successAgain")}
          <ArrowRight className="size-4" weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="group/form relative overflow-hidden rounded-[1.75rem] bg-white p-7 transition-shadow duration-500 focus-within:shadow-[0_32px_70px_-34px_oklch(37.12%_0.059_186.62_/_0.4)] sm:p-9"
      style={{
        border: "1px solid var(--color-warm-border)",
        boxShadow:
          "0 1px 0 oklch(96% 0.01 84), 0 24px 52px -34px oklch(37.12% 0.059 186.62 / 0.22)",
      }}
    >
      <AccentEdge />

      {/* Header */}
      <div
        className="flex items-start justify-between gap-4 border-b pb-5"
        style={{ borderColor: "var(--color-warm-border)" }}
      >
        <div>
          <span
            className="text-[10px] font-semibold tracking-[0.22em] uppercase"
            style={{ color: "var(--color-section-label)" }}
          >
            {t("contactPage.form.sectionTitle")}
          </span>
          <h3 className="font-heading mt-1.5 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
            {t("footer.contactForm.title")}
          </h3>
        </div>
        <span
          className="hidden size-11 shrink-0 items-center justify-center rounded-2xl sm:inline-flex"
          style={{
            background: "var(--color-brand-bg-light)",
            color: "var(--color-brand-dark)",
          }}
        >
          <PaperPlaneTilt weight="duotone" className="size-5" />
        </span>
      </div>

      {/* Audience segmented control */}
      <div className="mt-6 flex flex-col gap-2">
        <Label className="text-[13px] font-medium text-foreground">
          {t("footer.contactForm.audienceLabel")}
        </Label>
        <div
          className="relative grid grid-cols-2 rounded-full p-1"
          style={{
            background: "oklch(96% 0.012 84)",
            border: "1px solid var(--color-warm-border)",
          }}
        >
          {/* sliding active indicator */}
          <span
            aria-hidden
            className="absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full transition-transform duration-300 ease-out"
            style={{
              transform:
                form.audience === "therapist"
                  ? "translateX(100%)"
                  : "translateX(0)",
              background:
                "linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand-dark) 100%)",
              boxShadow: "0 6px 16px var(--color-brand-shadow)",
            }}
          />
          {(
            [
              {
                key: "client",
                label: t("footer.contactForm.audienceClient"),
                Icon: User,
              },
              {
                key: "therapist",
                label: t("footer.contactForm.audienceTherapist"),
                Icon: Brain,
              },
            ] as const
          ).map(({ key, label, Icon }) => {
            const active = form.audience === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => update("audience", key)}
                className="relative z-10 flex items-center justify-center gap-2 rounded-full py-2 text-[13px] font-medium transition-colors duration-200"
                style={{
                  color: active ? "oklch(100% 0 0)" : "oklch(45% 0.025 84)",
                }}
              >
                <Icon weight={active ? "fill" : "regular"} className="size-4" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Name + email */}
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label={t("footer.contactForm.nameLabel")}>
          <Input
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder={t("footer.contactForm.namePlaceholder")}
            required
            minLength={2}
            maxLength={120}
            autoComplete="name"
            className="h-11"
          />
        </Field>
        <Field label={t("footer.contactForm.emailLabel")}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder={t("footer.contactForm.emailPlaceholder")}
            required
            maxLength={200}
            autoComplete="email"
            className="h-11"
          />
        </Field>
      </div>

      {/* Message */}
      <div className="mt-5">
        <Field label={t("footer.contactForm.messageLabel")}>
          <Textarea
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            placeholder={t("footer.contactForm.messagePlaceholder")}
            required
            minLength={10}
            maxLength={2000}
            rows={5}
            className="min-h-32 resize-y"
          />
        </Field>
      </div>

      {/* Honeypot — hidden from humans, visible to naive bots */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => update("website", e.target.value)}
          />
        </label>
      </div>

      {status === "error" && (
        <div
          className="mt-6 flex items-start gap-3 rounded-xl border p-4"
          style={{
            borderColor: "oklch(80% 0.08 27)",
            background: "oklch(97% 0.02 27)",
          }}
        >
          <Warning
            weight="duotone"
            className="mt-0.5 size-5 shrink-0"
            style={{ color: "oklch(50% 0.18 27)" }}
          />
          <div
            className="text-sm leading-relaxed"
            style={{ color: "oklch(40% 0.16 27)" }}
          >
            {errorMessage ?? t("footer.contactForm.errorBody")}
          </div>
        </div>
      )}

      <p className="mt-7 text-xs leading-relaxed text-muted-foreground">
        {t("footer.contactForm.consent")}
      </p>

      <div className="mt-5 flex items-center justify-end">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="group inline-flex h-12 items-center justify-center gap-2.5 rounded-full bg-primary px-9 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
          style={{ boxShadow: "0 4px 24px var(--color-brand-shadow)" }}
        >
          {status === "submitting"
            ? t("footer.contactForm.submitting")
            : t("footer.contactForm.submit")}
          <ArrowRight
            className="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
            weight="bold"
          />
        </button>
      </div>
    </form>
  );
}

/** Gold-to-teal hairline along the top edge — echoes the app's accent motif. */
function AccentEdge() {
  return (
    <span
      aria-hidden
      className="absolute inset-x-0 top-0 h-[3px]"
      style={{
        background:
          "linear-gradient(90deg, var(--color-accent-gold) 0%, var(--color-brand) 45%, var(--color-brand-dark) 100%)",
      }}
    />
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-[13px] font-medium text-foreground">{label}</Label>
      {children}
    </div>
  );
}
